// Hand-rolled inline SVG sparkline.  Tiny (default 96×24), no library.
// Renders a smooth path + optional filled area + last-point marker.

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';
import './charts.css';

function pathFromValues(values, width, height, padding) {
  if (!values || values.length === 0) return { line: '', area: '', points: [] };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = (width - padding * 2) / Math.max(1, values.length - 1);
  const points = values.map((v, i) => {
    const x = padding + i * step;
    const y = padding + (1 - (v - min) / range) * (height - padding * 2);
    return { x, y };
  });
  // Smooth Catmull-Rom-ish via quadratic mids.
  let line = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const mx = (prev.x + cur.x) / 2;
    line += ` Q ${prev.x.toFixed(2)} ${prev.y.toFixed(2)} ${mx.toFixed(2)} ${(
      (prev.y + cur.y) /
      2
    ).toFixed(2)}`;
    if (i === points.length - 1) {
      line += ` T ${cur.x.toFixed(2)} ${cur.y.toFixed(2)}`;
    }
  }
  const area =
    `M ${points[0].x.toFixed(2)} ${(height - padding).toFixed(2)} L ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} ` +
    line.replace(/^M [^L]+/, '') +
    ` L ${points[points.length - 1].x.toFixed(2)} ${(height - padding).toFixed(2)} Z`;
  return { line, area, points, last: points[points.length - 1] };
}

export function Sparkline({
  values = [],
  width = 120,
  height = 32,
  stroke,
  fill,
  tone = 'blue',
  showDot = true,
  ariaLabel,
  pointPayloads = null,
  surface = 'sparkline',
  seriesId = 'series',
  entityLabel = null,
}) {
  const padding = 3;
  const { line, area, points, last } = pathFromValues(values, width, height, padding);
  const tooltip = useOptionalRichTooltipController();
  const cls = `sparkline tone-${tone}`;
  return (
    <svg
      className={cls}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel || `Trend chart with ${values.length} points`}
    >
      {area ? <path d={area} className="spark-area" fill={fill || 'currentColor'} /> : null}
      {line ? (
        <path
          d={line}
          className="spark-line"
          fill="none"
          stroke={stroke || 'currentColor'}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      {showDot && last ? (
        <circle
          cx={last.x}
          cy={last.y}
          r="2.6"
          className="spark-dot"
          fill={stroke || 'currentColor'}
        />
      ) : null}
      {Array.isArray(pointPayloads) && tooltip
        ? pointPayloads.map((payload, index) => {
            const point = points[index];
            if (!payload || !point) return null;
            const triggerProps = tooltip.getTriggerProps({
              surfaceId: payload.surfaceId,
              payload,
              testIdPrefix: 'rich-tooltip',
              ariaLabel: `${entityLabel || ariaLabel || 'Sparkline point'} ${payload.sliceLabel || index + 1}`,
            });
            return (
              <circle
                key={payload.surfaceId}
                cx={point.x}
                cy={point.y}
                r="6"
                fill="transparent"
                className="rich-tooltip-svg-trigger sparkline-point-trigger"
                data-testid={`sparkline-point-${surface || 'sparkline'}-${seriesId || 'series'}-${index}`}
                role="button"
                tabIndex={0}
                {...triggerProps}
              />
            );
          })
        : null}
    </svg>
  );
}

// Hand-rolled mini bar chart for compact comparisons.
export function MiniBars({ values = [], width = 120, height = 32, tone = 'blue', ariaLabel }) {
  if (!values.length) return null;
  const max = Math.max(...values.map((v) => Math.abs(v))) || 1;
  const padding = 2;
  const gap = 2;
  const barCount = values.length;
  const barWidth = (width - padding * 2 - gap * (barCount - 1)) / barCount;
  const zero = height - padding - ((0 - -max) / (2 * max)) * (height - padding * 2);
  return (
    <svg
      className={`mini-bars tone-${tone}`}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel || `Comparison chart with ${values.length} bars`}
    >
      {values.map((v, i) => {
        const x = padding + i * (barWidth + gap);
        const top = padding + ((max - v) / (2 * max)) * (height - padding * 2);
        const h = Math.abs(zero - top) || 1;
        const cls = v >= 0 ? 'mini-bar pos' : 'mini-bar neg';
        return (
          <rect
            key={i}
            x={x}
            y={Math.min(top, zero)}
            width={barWidth}
            height={h}
            rx="1.2"
            className={cls}
          />
        );
      })}
    </svg>
  );
}
